import React, { useImperativeHandle, forwardRef, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Tooltip,
  IconButton,
} from '@mui/material';
import * as yup from 'yup';
// NOTE: You must install jsoneditor-react: npm install jsoneditor-react jsoneditor
// @ts-ignore
import { JsonEditor } from 'jsoneditor-react';
import 'jsoneditor-react/es/editor.min.css';
import { Icon } from '@iconify/react';
import './SimpleAuditForm.css';

// Yup validation schema for API input data
const apiInputSchema = yup.object({
  action: yup.string().oneOf(['finalize', 'reject'], 'Action must be either "finalize" or "reject"').required('Action is required'),
  notes: yup.string().required('Notes is required'),
  parsed_data: yup.object().required('Parsed data is required'),
  confidence_score: yup.number().required('Confidence score is required').min(0, 'Confidence score must be >= 0').max(100, 'Confidence score must be <= 1')
});

interface SimpleAuditFormProps {
  initialData: any;
  onValidationChange: (isValid: boolean, errors: string[], issues: string[]) => void;
  onChange: (data: any) => void;
  onSwitchModes: (data: any, mode: 'form' | 'json') => void;
}

const SimpleAuditForm = forwardRef<any, SimpleAuditFormProps>(({ initialData, onValidationChange, onChange, onSwitchModes }, ref) => {
  const [jsonValid, setJsonValid] = React.useState(true);
  const [jsonErrors, setJsonErrors] = React.useState<string[]>([]);
  const lastJsonData = useRef<any>(initialData);

  // Extract individual fields from JSON data for validation
  const extractFieldsFromJson = (jsonData: any) => {
    return {
      action: jsonData.action || 'finalize',
      notes: jsonData.notes || '',
      confidence_score: jsonData.confidence_score || 0,
      parsed_data: jsonData.parsed_data || {}
    };
  };

  // Custom validation for ticket_details
  const validateTicketDetails = (parsedData: any): string[] => {
    const issues: string[] = [];
    const ticketDetails = parsedData?.final_json?.ticket_details;
    if (!ticketDetails || !Array.isArray(ticketDetails)) return issues;

    // Check if games are in ascending order
    for (let i = 1; i < ticketDetails.length; i++) {
      const prevGame = ticketDetails[i - 1].game;
      const currGame = ticketDetails[i].game;
      if (prevGame !== undefined && currGame !== undefined && Number(currGame) < Number(prevGame)) {
        issues.push(`games not in ascending order: ${prevGame} -> ${currGame}`);
      }
    }

    // Validate each ticket
    ticketDetails.forEach((ticket: any, idx: number) => {
      const entry = `Entry ${idx + 1}`;
      // Game: 4 digits
      if (!/^\d{4}$/.test(String(ticket.game))) {
        issues.push(`${entry}: game '${ticket.game}' is not a 4-digit number.`);
      }
      // Pack: 7 digits
      if (!/^\d{7}$/.test(String(ticket.pack))) {
        issues.push(`${entry}: pack '${ticket.pack}' is not a 7-digit number.`);
      }
      // Range: 7 chars with dash (e.g., 999-999 or 999-END)
      if (!/^.{3}-.{3,4}$/.test(String(ticket.range)) || String(ticket.range).length !== 7) {
        issues.push(`${entry}: range '${ticket.range}' is not 7 characters with dash.`);
      }
    });

    return issues;
  };

  // Validation function
  const validateAndUpdateStatus = useCallback(async (jsonData: any) => {
    // First check if JSON is valid
    if (!jsonValid) {
      onValidationChange(false, jsonErrors, []);
      return false;
    }

    try {
      const extractedData = extractFieldsFromJson(jsonData);

      // Validate using Yup schema
      await apiInputSchema.validate(extractedData, { abortEarly: false });

      // Custom ticket_details validation
      const issues = validateTicketDetails(extractedData.parsed_data);
      const isValid = issues.length === 0;
      onValidationChange(isValid, [], issues);
      return isValid;
    } catch (error: any) {
      if (error instanceof yup.ValidationError) {
        const errors = error.inner.map((err: any) => err.message);
        // Custom ticket_details validation (even if schema fails)
        const extractedData = extractFieldsFromJson(jsonData);
        const issues = validateTicketDetails(extractedData.parsed_data);
        onValidationChange(false, errors, issues);
        return false;
      }
    }
    return false;
  }, [jsonValid, jsonErrors, onValidationChange]);

  // Expose validate method to parent
  useImperativeHandle(ref, () => ({
    validate: async () => {
      return await validateAndUpdateStatus(lastJsonData.current);
    }
  }));

  // Handle JSON changes
  const handleJsonChange = useCallback((value: any) => {
    lastJsonData.current = value;
    // Update parent with the complete JSON data
    onChange(value);

    // Validate and update validation status
    validateAndUpdateStatus(value);
  }, [onChange, validateAndUpdateStatus]);

  return (
    <Box className="SimpleAuditForm" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Overall Validation Status */}

      {/* Header with OCR Data and Switch Buttons */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" gutterBottom fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          OCR Data
          {initialData && (
            <Tooltip
              title={initialData.parsed_data?.final_json?.message || ''}
              placement="right"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: 'black',
                    color: 'white',
                    fontSize: 14,
                    maxWidth: 300,
                    p: 1.5,
                  },
                },
              }}
            >
              <Box
                sx={{
                  bgcolor: 'black',
                  color: 'white',
                  borderRadius: 1,
                  px: 1.5,
                  py: 0.5,
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: 'pointer',
                  ml: 1,
                  display: 'inline-block',
                }}
              >
                {initialData?.parsed_data?.final_json?.confidence
                  ? initialData?.parsed_data?.final_json?.confidence + '%'
                  : initialData?.confidence_score + '%'}
              </Box>
            </Tooltip>
          )}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Table View">
            <IconButton
              color={'default'}
              sx={{ backgroundColor: 'transparent' }}
              onClick={() => {
                let currentData = lastJsonData.current;
                onSwitchModes(currentData, 'form');
              }}
            >
              <Icon icon="fluent:form-new-28-regular" fontSize={32} />
            </IconButton>
          </Tooltip>

          <Tooltip title="JSON View">
            <IconButton
              color={'primary'}
              sx={{ backgroundColor: 'rgba(44, 108, 160, 0.1)' }}
            // onClick={handleSwitchToJson}
            >
              <Icon icon="mdi:code-json" fontSize={32} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box >

        {jsonErrors.length > 0 && (
          <Typography variant="caption" color="error.main">
            {jsonErrors[0]}
          </Typography>
        )}
      </Box>

      {/* JSON Editor Section */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: '1200px', maxHeight: '1300px', overflow: 'auto', marginTop: '30px' }}>
        <JsonEditor
          value={initialData}
          mode="tree"
          onChange={handleJsonChange}
          mainMenuBar={true}
          navigationBar={true}
          history={true}
          allowedModes={["tree", "code"]}
          enableSort={false}
          enableTransform={false}
          indentation={2}
          onValidationError={(errors: any) => setJsonErrors(errors.map((e: any) => e.message))}
          height="1200px"
          expandAll={true}
        />
      </Box>
    </Box>
  );
});

export default SimpleAuditForm; 
